import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppDataKeyField } from '@/form/AppDataKeyField';
import {
    AppCheckboxField,
    AppEquipmentTypeSelectField,
    AppInfoTypeSelectField,
    AppInputField,
    AppPromptsField,
    AppSelectField,
} from '@/form/FormUI';
import type { NodeFormProps, YesNoNodeType } from '@/nodes';
import { equipmentTypesState, infoTypesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { YesNoDataSchema, type YesNoDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof YesNoDataSchema>;

const YesNoForm: FC<NodeFormProps<YesNoNodeType>> = ({ spaceId, flowId, node, updateNode, cancelUpdate }) => {
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));
    const infoTypes = useAsyncAtomValue(infoTypesState(spaceId));

    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, YesNoDataType>({
        resolver: zodResolver(YesNoDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: YesNoDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppPromptsField description="What are we asking the user to choose between?" />

                    <AppDataKeyField
                        control={form.control}
                        name="dataKey"
                        label="Data Key"
                        placeholder="field_name"
                        description="The name used to reference this value in code."
                        mode="produce"
                        currentNodeId={node.id}
                    />

                    <AppInfoTypeSelectField
                        control={form.control}
                        name="infoTypeId"
                        label="Info Type"
                        description="Store the selected option as the value of an Info Type."
                        infoTypes={infoTypes}
                        placeholder="Select Info Type (Optional)"
                    />

                    <AppInputField
                        label="Yes Label"
                        placeholder="Yes"
                        control={form.control}
                        name="yesLabel"
                        description="The label on the 'yes' button."
                    />

                    <AppInputField
                        label="No Label"
                        placeholder="No"
                        control={form.control}
                        name="noLabel"
                        description="The label on the 'no' button."
                    />

                    <AppCheckboxField label="'No' Option First" control={form.control} name="noFirst" />
                    <AppCheckboxField
                        label="Force Selection (hide 'Not Sure' option)"
                        control={form.control}
                        name="forceSelection"
                    />

                    <AppSelectField
                        label="Help Type"
                        control={form.control}
                        name="helpType"
                        description="What kind of Help button to show."
                        options={['help-me-decide', 'model-number-help', 'none']}
                    />

                    {watchedFields.helpType === 'model-number-help' && (
                        <AppEquipmentTypeSelectField
                            control={form.control}
                            name="equipmentTypeId"
                            label="Equipment Type"
                            description="Select an Equipment Type that the user will get help finding the model number for"
                            equipmentTypes={equipmentTypes}
                            allowContextSelection
                        />
                    )}
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default YesNoForm;
